import { forwardRef } from 'react';

import { Box, type BoxProps, useTheme } from '@frontend/legacy-components';

import { useAppPanelContainerContext } from '../context';

export interface ResizeHandleProps extends BoxProps {
  onResizeMouseDown?: (e: React.MouseEvent) => void;
  onResizeDoubleClick?: () => void;
  isResizing?: boolean;
  side?: 'left' | 'right';
}

export const ResizeHandle = forwardRef<HTMLDivElement, ResizeHandleProps>(
  (
    {
      onResizeMouseDown: onResizeMouseDownProp,
      onResizeDoubleClick: onResizeDoubleClickProp,
      isResizing: isResizingProp,
      side = 'left',
      ...props
    },
    ref,
  ) => {
    const context = useAppPanelContainerContext();
    const theme = useTheme();
    const { isResizable, isVisible, isCollapsed } = context;

    // Don't render if panel is not resizable, not visible, or collapsed
    if (!isResizable || !isVisible || isCollapsed) {
      return null;
    }

    const onMouseDown = onResizeMouseDownProp ?? context.onResizeMouseDown;
    const onDoubleClick =
      onResizeDoubleClickProp ?? context.onResizeDoubleClick;
    const isResizing = isResizingProp ?? context.isResizing;
    const resizeHandleWidth = theme.space[3];

    const positionProps =
      side === 'left'
        ? { left: `calc(-1 * ${resizeHandleWidth} / 2)` }
        : { right: `calc(-1 * ${resizeHandleWidth} / 2)` };

    return (
      <Box
        ref={ref}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        position="absolute"
        top={0}
        height="100%"
        width={`calc(${resizeHandleWidth} + 1px)`}
        cursor="col-resize"
        zIndex={10}
        userSelect="none"
        opacity={isResizing ? 1 : 0}
        _hover={{
          opacity: 1,
        }}
        transition="opacity 0.2s"
        display="flex"
        alignItems="center"
        justifyContent="center"
        _before={{
          content: '""',
          width: `calc(${theme.space[0.25]} + 1px)`,
          height: '100%',
          background: 'interaction.main',
        }}
        {...positionProps}
        {...props}
      />
    );
  },
);

ResizeHandle.displayName = 'ResizeHandle';
